import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ProposalList } from 'ts/shared/interfaces/ProposalData';
import { stripTags } from 'utils/shared/utils';
import ScreenshotSupport from '../../ScreenshotSupport';
import { checkAHAVI } from 'utils/utils';

interface FreeTextModalProps {
  close: () => void;
  onSave: (_: any) => void;
  proposalData?: ProposalList;
}

const FreeTextModal: FC<FreeTextModalProps> = ({ close, onSave, ...props }) => {
  const { t } = useTranslation();
  const [desc, setDesc] = useState('');
  const [displayOrder, setDisplayOrder] = useState('');
  const [price, setPrice] = useState('');
  const [prices, setPrices] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [descFlt, setDescFlt] = useState('');
  const [descTick, setDescTick] = useState('');
  const [isDynamic, setIsDynamic] = useState(false);
  const [showCancellationDate, setShowCancellationDate] = useState(true);
  const [cancellationDateDelta, setCancellationDateDelta] = useState('0');
  const isHotelProduct =
    props.proposalData?.listOfItems?.length === 1 &&
    props.proposalData?.listOfItems?.[0]?.proposal.code === 'HTL';
  const flightIndex = props.proposalData?.listOfItems.findIndex(
    (item) => item.proposal.code === 'FLT'
  );
  const ticketIndex = props.proposalData?.listOfItems.findIndex(
    (item) => item.proposal.code === 'SACT'
  );

  useEffect(() => {
    const items = props.proposalData?.listOfItems;
    const item = items?.[0];
    if (item) {
      if (items.length > 1) {
        setIsDynamic(true);
        const htlIndex = items.findIndex((item) => item.proposal.code === 'HTL');
        setDesc(items[htlIndex].hotels[0].displayNote?.name || '');
        setDisplayOrder(String(items[htlIndex].hotels[0].displayOrder ?? ''));

        const tickIndex = items.findIndex((item) => item.proposal.code === 'SACT');
        setDescTick(items[tickIndex]?.sportActivites?.[0]?.displayNote?.name || '');

        const fltIndex = items.findIndex((item) => item.proposal.code === 'FLT');
        setDescFlt(items[fltIndex]?.flights?.[0]?.displayNote?.code || '');
      } else if (item.proposal.code === 'HTL') {
        setDesc(item.hotels[0].displayNote?.name || '');
        setDisplayOrder(String(item.hotels[0].displayOrder ?? ''));
      } else if (item.proposal.code === 'FLT') {
        setDesc(item.flights[0].displayNote?.code || '');
        setDisplayOrder(String(item.flights[0].displayOrder ?? ''));
      } else if (item.proposal.code === 'TRS') {
        setDesc(item.transfers[0].displayNote?.name || '');
        setDisplayOrder(String(item.transfers[0].displayOrder ?? ''));
      } else if (item.proposal.code === 'PCK') {
        setDesc(item.packages[0].displayNote?.name || '');
        setDisplayOrder(String(item.packages[0].displayOrder ?? ''));
      } else if (item.proposal.code === 'SACT' && item.sportActivites?.length) {
        setDesc(item.sportActivites[0].displayNote?.name || '');
        setDisplayOrder(String(item.sportActivites[0].displayOrder ?? ''));
      }
    }

    if (items?.length === 1 && item?.proposal.code === 'HTL') {
      const prices = props.proposalData?.listOfItems[0].hotels[0].rooms.map(
        (room) => room.supplements?.[0]?.code || ''
      );
      setPrices(prices || []);
      setShowCancellationDate(props.proposalData?.showCancellationDate ?? true);
      setCancellationDateDelta(String(props.proposalData?.cancellationDateDelta ?? 0));
    } else {
      setPrice(props.proposalData?.salePrice + '');
    }
  }, [props.proposalData]);

  const onClickSave = () => {
    setIsSubmitted(true);
    const clearDesc = stripTags(desc);
    const clearDescFlt = stripTags(descFlt);
    const clearDescTick = stripTags(descTick);
    if (
      displayOrder.length > 2 ||
      (clearDesc && clearDesc.length < 3) ||
      (clearDescFlt && clearDescFlt.length < 3) ||
      (clearDescTick && clearDescTick.length < 3)
    ) {
      return;
    }

    const items = props.proposalData?.listOfItems;
    const item = items?.[0];
    let newItem = null;
    let newItems = [...(items || [])];

    if (items?.length && items.length > 1) {
      const htlIndex = items.findIndex((item) => item.proposal.code === 'HTL');
      const htlItem = items[htlIndex];

      newItems[htlIndex] = {
        ...htlItem,
        hotels: [
          {
            ...htlItem.hotels[0],
            displayNote: {
              ...htlItem.hotels[0].displayNote!,
              name: clearDesc,
            },
            displayOrder: displayOrder ? +displayOrder : undefined,
          },
        ],
      };

      if (flightIndex && flightIndex !== -1) {
        const fltItem = items[flightIndex];

        if (fltItem.flights?.[0]?.displayNote) {
          newItems[flightIndex] = {
            ...fltItem,
            flights: [
              {
                ...fltItem.flights[0],
                displayNote: {
                  ...fltItem.flights[0].displayNote,
                  code: clearDescFlt,
                },
                displayOrder: displayOrder ? +displayOrder : undefined,
              },
            ],
          };
        }
      }

      if (ticketIndex && ticketIndex !== -1) {
        const tickItem = items[ticketIndex];

        if (tickItem.sportActivites?.[0]?.displayNote) {
          newItems[ticketIndex] = {
            ...tickItem,
            sportActivites: [
              {
                ...tickItem.sportActivites[0],
                displayNote: {
                  ...tickItem.sportActivites[0].displayNote!,
                  name: clearDescTick,
                },
                displayOrder: displayOrder ? +displayOrder : undefined,
              },
            ],
          };
        }
      }
    } else if (item?.proposal.code === 'HTL') {
      newItem = {
        ...item,
        hotels: [
          {
            ...item.hotels[0],
            displayNote: {
              ...item.hotels[0].displayNote,
              name: clearDesc,
            },
            displayOrder: displayOrder ? +displayOrder : undefined,
            rooms: item.hotels[0].rooms.map((room, index) => ({
              ...room,
              supplements: [
                {
                  ...room.supplements?.[0],
                  code: prices[index],
                },
              ],
            })),
          },
        ],
      };
    } else if (item?.proposal.code === 'FLT') {
      newItem = {
        ...item,
        flights: [
          {
            ...item.flights[0],
            displayNote: {
              ...item.flights[0].displayNote,
              code: clearDesc,
            },
            displayOrder: displayOrder ? +displayOrder : undefined,
          },
        ],
      };
    } else if (item?.proposal.code === 'TRS') {
      newItem = {
        ...item,
        transfers: [
          {
            ...item.transfers[0],
            displayNote: {
              ...item.transfers[0].displayNote,
              name: clearDesc,
            },
            displayOrder: displayOrder ? +displayOrder : undefined,
          },
        ],
      };
    } else if (item?.proposal.code === 'PCK') {
      newItem = {
        ...item,
        packages: [
          {
            ...item.packages[0],
            displayNote: {
              ...item.packages[0].displayNote,
              name: clearDesc,
            },
            displayOrder: displayOrder ? +displayOrder : undefined,
          },
        ],
      };
    } else if (item?.proposal.code === 'SACT' && item.sportActivites?.length) {
      newItem = {
        ...item,
        sportActivites: [
          {
            ...item.sportActivites[0],
            displayNote: {
              ...item.sportActivites[0].displayNote,
              name: clearDesc,
            },
            displayOrder: displayOrder ? +displayOrder : undefined,
          },
        ],
      };
    }

    const newPropolalItem = {
      ...props.proposalData,
      edited: true,
      ...(isHotelProduct
        ? {
          showCancellationDate,
          cancellationDateDelta: +cancellationDateDelta,
        }
        : {
          salePrice: price ? +price : props.proposalData?.salePrice,
          amount: price ? +price : props.proposalData?.amount,
        }),
      listOfItems: items?.length && items.length > 1 ? newItems : [newItem],
    };

    if (newPropolalItem) {
      onSave(newPropolalItem);
    }

    close();
  };

  return (
    <Fragment>
      {createPortal(
        <div className="modal-overlay modal-overlay-top">
          <div className="modal active" style={{ width: '100%', maxWidth: 720 }}>
            <div className="modal-title">
              <div className="title">{t('proposal.edit')}</div>
              <div>
                <ScreenshotSupport />
                <FontAwesomeIcon icon={faTimes} onClick={close} />
              </div>
            </div>
            <div className="modal-content">
              <div
                className={
                  'form-group mb-15 ' + (isSubmitted && desc.length < 3 ? ' is-invalid' : '')
                }
              >
                <label>
                  {isDynamic ? t('hotel.hotel') : ''} {t('notes')}
                </label>
                <textarea
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  className="form-control form-control-textarea"
                  style={{ direction: checkAHAVI(desc) ? 'rtl' : 'ltr', height: 90 }}
                  rows={3}
                />
                {isSubmitted && desc.length < 3 && (
                  <div className="form-invalid-text">{t('validation.required')}</div>
                )}
              </div>
              {isDynamic && (
                <Fragment>
                  {flightIndex !== -1 && (
                    <div
                      className={
                        'form-group mb-15' +
                        (isSubmitted && descFlt.length < 3 ? ' is-invalid' : '')
                      }
                    >
                      <label>
                        {t('flight.flight')} {t('notes')}
                      </label>
                      <textarea
                        value={descFlt}
                        onChange={(e) => setDescFlt(e.target.value)}
                        className="form-control form-control-textarea"
                        style={{ direction: checkAHAVI(descFlt) ? 'rtl' : 'ltr', height: 90 }}
                        rows={3}
                      />
                      {isSubmitted && descFlt.length < 3 && (
                        <div className="form-invalid-text">{t('validation.required')}</div>
                      )}
                    </div>
                  )}
                  {ticketIndex !== -1 && (
                    <div
                      className={
                        'form-group mb-15' +
                        (isSubmitted && descTick.length < 3 ? ' is-invalid' : '')
                      }
                    >
                      <label>
                        {t('dyn.ticket')} {t('notes')}
                      </label>
                      <textarea
                        value={descTick}
                        onChange={(e) => setDescTick(e.target.value)}
                        className="form-control form-control-textarea"
                        style={{ direction: checkAHAVI(descTick) ? 'rtl' : 'ltr', height: 90 }}
                        rows={3}
                      />
                      {isSubmitted && descTick.length < 3 && (
                        <div className="form-invalid-text">{t('validation.required')}</div>
                      )}
                    </div>
                  )}
                </Fragment>
              )}
              <div className="mb-15">
                <FormGroup
                  label={t('proposal.display_order')}
                  value={displayOrder}
                  onChange={setDisplayOrder}
                  validation={{ numbers: true, maxLength: 2 }}
                  isSubmitted={isSubmitted}
                />
              </div>
              {isHotelProduct && (
                <Fragment>
                  <div className="mb-15">
                    <Checkbox
                      checked={showCancellationDate}
                      label={t('proposal.show_cancellation_date')}
                      onChange={setShowCancellationDate}
                    />
                  </div>
                  <div className="mb-15">
                    <div className='mb-5'>{t('proposal.cancellation_date_delta')}</div>
                    <SelectCustom
                      options={[0, 1, 2, 3, 4, 5].map((n) => ({
                        label: String(n),
                        value: String(n),
                      }))}
                      value={cancellationDateDelta}
                      onChange={setCancellationDateDelta}
                      menuPlacement='top'
                    />
                  </div>
                </Fragment>
              )}
              {isHotelProduct ? (
                <div className="mb-15">
                  {prices.map((price, index) => (
                    <div className="mb-10" key={index}>
                      <FormGroup
                        label={t('price') + ' #' + (index + 1) + ' ' + t('hotel.room')}
                        value={price}
                        onChange={(val) => {
                          const newPrices = [...prices];
                          newPrices[index] = val;
                          setPrices(newPrices);
                        }}
                        validation={{ numbers: true }}
                        isSubmitted={isSubmitted}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mb-15">
                  <FormGroup
                    label={t('price')}
                    value={price}
                    onChange={setPrice}
                    validation={{ numbers: true }}
                    isSubmitted={isSubmitted}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button className="btn btn-default" title={t('close')} onClick={close} />

              <Button className="btn btn-primary" title={t('save')} onClick={onClickSave} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </Fragment>
  );
};

export default FreeTextModal;
